<script lang="ts" setup>
import { dragscroll as vDragscroll } from 'vue-dragscroll'
import type UIChipsWrapper from '@design-system/components/UI/UIChipsWrapper.vue'
import type { EditorialTimelineContainerProps } from './EditorialTimelineContainer.props'
import type { WrapperChipType } from '@design-system/components/UI/UIChipsWrapper.props'

// Declare scrollEndTimer in the global window object
declare global {
  interface Window {
    scrollEndTimer: NodeJS.Timeout
  }
}

const props = defineProps<EditorialTimelineContainerProps>()

const { breakpoint } = useProjectBreakpoint()
const isDesktop = breakpoint.greater('md')

const chips = ref<WrapperChipType[]>(
  props.blocks.map((block, index) => {
    return {
      label: block.titleBlock,
      selected: index === 0,
    }
  })
)

const blockRefs = ref<(HTMLElement | null)[]>([])
const chipsWrapperRef = ref<InstanceType<typeof UIChipsWrapper> | null>(null)
const timelineBlocksContainerRef = ref<HTMLElement | null>()
const lockScroll = ref(false)
const { onDragScrollStart, onDragScrollMove, onDragScrollEnd } = useDragInertia(timelineBlocksContainerRef)

const scrollBlock = (selectedIndex: number) => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const selectedBlock = blockRefs.value[selectedIndex]

      if (selectedBlock && timelineBlocksContainerRef.value) {
        const container = timelineBlocksContainerRef.value
        const paddingLeft = parseInt(
          window.getComputedStyle(document.querySelector('.chip')!).paddingLeft
        )

        const scrollToPosition =
          selectedBlock.getBoundingClientRect().left -
          container.getBoundingClientRect().left +
          container.scrollLeft -
          paddingLeft

        container.scrollTo({
          left: scrollToPosition,
          behavior: 'smooth',
        })
        // TODO: Wait for scrollend full compatibility on Safari and Mobile browsers and remove on scroll
        // useEventListener(
        //   container,
        //   'scrollend',
        //   () => (lockScroll.value = false),
        //   { once: true }
        // )

        container.onscroll = () => {
          clearTimeout(window.scrollEndTimer)
          window.scrollEndTimer = setTimeout(
            () => (lockScroll.value = false),
            100
          )
        }
      }
    })
  })
}

const handleSelectYear = async (index: number) => {
  lockScroll.value = true
  chipsWrapperRef.value?.changeSelectedChip(index)
  scrollBlock(index)
}

onMounted(() => {
  blockRefs.value.forEach((item, index) => {
    useIntersectionObserver(
      item,
      ([entry]) => {
        if (entry.isIntersecting && !lockScroll.value) {
          chipsWrapperRef.value?.changeSelectedChip(index)
        }
      },
      {
        threshold: [0, 0.5, 1.0],
        rootMargin: `0px -50% 0px -50%`,
      }
    )
  })
})
</script>

<template>
  <div class="bg-primitives-off-white py-16">
    <div class="grid-standard">
      <div class="col-span-12">
        <div
          class="mt-6 flex flex-col justify-center gap-4 text-center lg:mt-8 xl:mt-10"
        >
          <UtilsMarkdown
            v-if="title"
            :content="title"
            is-preprocessed
            container="h2"
            class="text-book-4 md:text-book-3"
          />
          <UtilsMarkdown
            v-if="description"
            :content="description"
            is-preprocessed
            container="p"
            class="text-light-5"
          />
        </div>
      </div>
    </div>
    <div class="timeline-grid timeline-grid-selector">
      <div class="col-span-12">
        <!-- years -->
        <UIChipsWrapper
          ref="chipsWrapperRef"
          class="my-6 lg:my-8 xl:my-10"
          :chips="chips"
          :default-selected-chip="0"
          :disable-internal-state="true"
          :is-timeline-style="true"
          @selected="
            (selectedChipIndex: number) => handleSelectYear(selectedChipIndex)
          "
        />
        <!-- cards -->
        <div
          ref="timelineBlocksContainerRef"
          v-dragscroll:nochilddrag.x="{ active: isDesktop }"
          class="scrollbar-hide timeline-blocks-container flex overflow-x-scroll"
          @dragscrollstart="onDragScrollStart"
          @dragscrollmove="onDragScrollMove"
          @dragscrollend="onDragScrollEnd"
        >
          <EditorialTimelineBlock
            v-for="(block, index) in blocks"
            :ref="
              (el: any) =>
                (blockRefs[index] = (el as ComponentPublicInstance).$el)
            "
            v-bind="block"
            :key="`block-year-${block.titleBlock}`"
            :index="index"
            :is-last-block="index === blocks.length - 1"
            :aspect-ratio="mediaAspectRatio"
            class="chip shrink-0"
            @selected="
              (selectedYearIndex: number) => handleSelectYear(selectedYearIndex)
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>
